import { Api } from '@/core/api.js'
import { db_timers, db_checks_to_sync } from '@/database'
import { Notification } from '@/core/notification.js'
import { v4 as uuidv4 } from 'uuid'
import i18n from '@/i18n'
import store from '@/store'
import { TIMER_TICK_FAST_MS, TIMER_TICK_SLOW_MS, TOOLS } from '@/constants'
import { buildDefaultLocalKpiCounts, buildRadarModuleState } from '@/store/utils/radarKpis.js'
import { getLocationMoment, getLocationTimestamp, parseLocationDateTime, floatToLocationTimestamp, parseLocationTimeOnDay, parseServerDateTime, isValidServerDateTime } from '@/utils/locationTime'
import { formatTimerAlarmFromStore } from '@/utils/dateFormatters'

/** Marca de persistencia offline/cache; siempre epoch real del dispositivo. */
function persistenceTimestamp() {
    return Date.now()
}
const notification = new Notification()

const api = new Api()

const getDefaultState = () => {
    return {
        timers: null,
        categories: null,
        checksToSync: [],
        interval: null,
        intervalMs: TIMER_TICK_SLOW_MS,
        locationNowTick: 0,
        manualTimersState: {} // Nuevo: estado de timers manuales
    }
}

// initial state
const state = getDefaultState()

const getters = {
    getManualTimerState: (state) => (timerId) => {
        return state.manualTimersState[timerId] || null
    },
    getTimers(state, rootState) {
        return state.timers
    },

    getTimerById: (state, rootState) => (id) => {
        return state.timers.find((timer) => timer.id === id)
    },
    getCategories(state, rootState) {
        return state.categories
    },
    getTimersPendings(state, rootState) {
        if (state.timers) return state.timers.filter((time) => time.isAvailable).sort((a, b) => a.previousAlarm - b.previousAlarm)
        else return []
    },
    getTimersCompleted(state, rootState) {
        if (state.timers) return state.timers.filter((time) => !time.isAvailable).sort((a, b) => a.nextAlarm - b.nextAlarm)
        else return []
    },
    getLocationNow(state, getters, rootState, rootGetters) {
        // locationNowTick invalida la caché de Vuex; sin esto el getter devuelve siempre el mismo instante.
        void state.locationNowTick
        return getLocationTimestamp(rootGetters)
    },
    /**
     * BUILD-7499: KPI local de Radar `operational_timers_expired`.
     * Cuenta timers vencidos que requieren firma/revisión (`getTimersPendings`).
     * Al leer el count en el getter externo, cualquier cambio en timers pendientes
     * (vence, se firma, loadTimers, updateTimers) invalida Radar y refresca módulo/Critical Card.
     */
    getRadarModuleState: (state, getters) => {
        const expiredOperationalTimersCount = (getters.getTimersPendings || []).length

        return (kpiKeys) => {
            const localKpiCounts = buildDefaultLocalKpiCounts(TOOLS.OPERATIONAL_TIMERS, kpiKeys)

            if ('operational_timers_expired' in localKpiCounts) {
                localKpiCounts.operational_timers_expired = expiredOperationalTimersCount
            }

            return buildRadarModuleState(TOOLS.OPERATIONAL_TIMERS, null, kpiKeys, localKpiCounts)
        }
    }
}

const actions = {
    // Guardar estado de timer manual
    saveManualTimerState(context, { timerId, stateData }) {
        const manualTimersState = { ...context.state.manualTimersState }
        manualTimersState[timerId] = {
            ...stateData,
            lastUpdated: persistenceTimestamp()
        }
        context.commit('setManualTimersState', manualTimersState)

        // Guardar también en almacenamiento local para persistencia
        localStorage.setItem('manualTimersState', JSON.stringify(manualTimersState))
    },

    // Cargar estado de timers manuales al iniciar
    loadManualTimersState(context) {
        const savedState = localStorage.getItem('manualTimersState')
        if (savedState) {
            try {
                const stateData = JSON.parse(savedState)
                context.commit('setManualTimersState', stateData)
            } catch (e) {
                console.error('Error loading manual timers state:', e)
            }
        }
    },

    // Limpiar estado de un timer manual cuando se completa
    clearManualTimerState(context, { timerId }) {
        const manualTimersState = { ...context.state.manualTimersState }
        delete manualTimersState[timerId]
        context.commit('setManualTimersState', manualTimersState)
        localStorage.setItem('manualTimersState', JSON.stringify(manualTimersState))
    },
    loadTimers(context, params) {
        if (!fnCheckConnection()) {
            return loadTimersOffline(context)
        }
        // const fakeTimers = [
        //     {
        //         id: 'cGxRSUZUNkxvQTVQTGdnL1JVQWtYZz09',
        //         name: 'Limpieza de trapos',
        //         description: 'Meter trapos en la lavadora',
        //         icon: 'https://alex-itw.s3.eu-west-1.amazonaws.com/gallery/timers/Timer_Icon_03.svg',
        //         color: '#d3d3d3',
        //         type: '1',
        //         order: '5',
        //         times: [13, 13.5, 14, 14.5, 15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5, 20],
        //         status: '1',
        //         timer_rel: '8',
        //         rel_id: '30'
        //     },
        //     {
        //         id: 'cGxRSUZUNkxvQTVQTGdnL1JVQWtYZz10',
        //         name: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean a dolor rutrum elit ultrices ullamcorper at non mauris. Phasellus nec neque dolor.',
        //         description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean a dolor rutrum elit ultrices ullamcorper at non mauris. Phasellus nec neque dolor.',
        //         icon: 'https://alex-itw.s3.eu-west-1.amazonaws.com/gallery/timers/Timer_Icon_03.svg',
        //         color: '#d3d3d3',
        //         type: '1',
        //         order: '5',
        //         times: [20],
        //         status: '1',
        //         timer_rel: '8',
        //         rel_id: '30'
        //     },
        //     {
        //         id: 'cGxRSUZUNkxvQTVQTGdnL1JVQWtYZz11',
        //         name: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean a dolor rutrum elit ultrices ullamcorper at non mauris. Phasellus nec neque dolor.',
        //         description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean a dolor rutrum elit ultrices ullamcorper at non mauris. Phasellus nec neque dolor.',
        //         icon: 'https://alex-itw.s3.eu-west-1.amazonaws.com/gallery/timers/Timer_Icon_03.svg',
        //         color: '#d3d3d3',
        //         type: '1',
        //         order: '5',
        //         times: [20],
        //         status: '1',
        //         timer_rel: '8',
        //         rel_id: '30'
        //     }
        // ]

        return api
            .get('timer-op/timers')
            .then((response) => {
                if (!response || response.data == null) {
                    return false
                }

                const timersArr = Object.values(response.data) //.filter((timer) => timer.id == 'NDc1OEcwOTZ2TDduTkg2cHlWcHBpdz09')

                // formattear y settear los Timers:
                context.dispatch('formatTimers', timersArr)

                // context.dispatch('formatTimers', fakeTimers)

                // actualizar los timers (pending alarm, next alarm, isAvailable):
                // BUILD-7499: await chain so Radar only marks lastSyncedAt after isAvailable is recalculated.
                return context
                    .dispatch('getLastTimesDone')
                    .then(() => context.dispatch('updateTimers'))
                    .then(() => {
                        const timers = [...(context.getters.getTimers || [])]
                        context.dispatch('generateNotifications', timers)

                        // Solo arrancar el intervalo lento si aún no existe; la vista Timers activa el tick rápido.
                        if (!context.state.interval) {
                            context.dispatch('setupInterval', { intervalMs: TIMER_TICK_SLOW_MS })
                        }

                        return true
                    })
            })
            .catch((error) => {
                // console.error(error)
                // return loadTimersOffline(context)
                console.error(error)

                return false
            })
    },

    loadCategories(context, params) {
        return api
            .get('admin/timer-op/categories')
            .then((response) => {
                context.commit('setCategories', response.data)
            })
            .catch((error) => {
                console.error(error)
            })
    },

    generateNotifications(context, timers) {
        return new Promise((resolve, reject) => {
            if (timers.length == 0) resolve()
            else {
                const timer = timers.pop()
                const locationNow = getLocationMoment(context.rootGetters)
                let notificationDate = timer.currentPendingAlarm && locationNow.isBefore(moment.utc(timer.currentPendingAlarm)) ? timer.currentPendingAlarm : timer.nextAlarm
                // log("add Timer notification info:", timer, moment(notificationDate).format('YYYY-MM-DD HH:mm:ss'), timer.currentPendingAlarm, timer.nextAlarm)
                // notification.addLocalNotification({
                //     title: 'Pendiente de firmar: ' + timer.name,
                //     description: timer.description,
                //     text: '¡Es hora de hacerlo!',
                //     group: i18n.t('tools.timer_title'),
                //     ongoing: true,
                //     trigger: { at: moment(notificationDate).toDate() },
                //     data: { type: 'timerOp', timerId: timer.id, date: moment(notificationDate).format('YYYY-MM-DD HH:mm:ss') }
                // })
                setTimeout(() => {
                    context.dispatch('generateNotifications', timers)
                }, 2000)
            }
        })
    },
    getLastTimesDone(context, params) {
        const timersInState = context.state.timers || []
        if (!timersInState || timersInState.length === 0) return Promise.resolve()

        const timersCopy = structuredClone(timersInState)
        const ids = timersCopy.map((t) => t.id).join(',')

        let serverMap = {}

        if (fnCheckConnection()) {
            return api
                .post('timer-op/timers/checked', { timers: ids })
                .then((res) => {
                    if (res && res.status && res.data) {
                        Object.values(res.data).forEach((row) => {
                            if (row && row.id) serverMap[row.id] = row
                        })
                    }
                })
                .catch((e) => {
                    console.error('getLastTimesDone error:', e)
                })
                .finally(() => {
                    timersCopy.forEach((timer) => {
                        let per = {}
                        try {
                            per = JSON.parse(timer.periodicity)
                        } catch (_) {}

                        const isManual = (per && per.typePeriodicity) === 'manual' || timer.isManual === true

                        if (isManual) {
                            // PARA TIMERS MANUALES: Priorizar estado local sobre servidor
                            const saved = (context.state.manualTimersState && context.state.manualTimersState[timer.id]) || null

                            if (saved) {
                                // Usar el estado LOCAL como fuente de verdad para timers manuales
                                timer.isAvailable = !!(saved.isFinished && !saved.actionCompleted)

                                // Preservar todos los datos del estado manual
                                if (saved.completedAt) timer.lastAlarmDone = saved.completedAt
                                if (saved.totalElapsed != null) timer.totalElapsed = saved.totalElapsed
                                if (saved.remainingSec != null) timer.remainingSec = saved.remainingSec
                                if (saved.hasStarted != null) timer.hasStarted = saved.hasStarted
                                if (saved.isRunning != null) timer.isRunning = saved.isRunning
                            } else {
                                // Si no hay estado local, inicializar como no disponible
                                timer.isAvailable = false
                                timer.lastAlarmDone = null
                                timer.totalElapsed = 0
                                timer.remainingSec = timer.durationMinutes ? timer.durationMinutes * 60 : 0
                            }
                            return // Saltar el procesamiento de timers normales
                        }

                        // TIMERS NORMALES (no manuales) - lógica original
                        const fromSrv = serverMap[timer.id]
                        let serverLast = null

                        if (fromSrv && fromSrv.last_done) {
                            serverLast = parseLocationDateTime(fromSrv.last_done)
                        }

                        const effectiveLast = Math.max(Number(timer.lastAlarmDone || 0), Number(serverLast || 0)) || null

                        timer.lastAlarmDone = effectiveLast

                        if (timer.currentPendingAlarm) {
                            if (effectiveLast && effectiveLast >= timer.currentPendingAlarm) {
                                timer.isAvailable = false
                            } else {
                                timer.isAvailable = true
                            }
                        }
                    })

                    // Actualizar el estado global
                    context.commit('setTimers', timersCopy)

                    // Persistir en base de datos local
                    return db_timers
                        .clear()
                        .then(() => {
                            timersCopy.forEach((t) =>
                                db_timers.setItem(t.id, {
                                    ...t,
                                    lastUpdated: persistenceTimestamp()
                                })
                            )
                        })
                        .catch((e) => console.error('Error persistiendo timers en getLastTimesDone:', e))
                })
        } else {
            // SIN CONEXIÓN: Respetar completamente el estado local
            timersCopy.forEach((timer) => {
                let per = {}
                try {
                    per = JSON.parse(timer.periodicity)
                } catch (_) {}
                const isManual = (per && per.typePeriodicity) === 'manual' || timer.isManual === true

                if (isManual) {
                    // Para timers manuales offline, priorizar estado local
                    const saved = (context.state.manualTimersState && context.state.manualTimersState[timer.id]) || null
                    if (saved) {
                        timer.isAvailable = !!(saved.isFinished && !saved.actionCompleted)
                        if (saved.completedAt) timer.lastAlarmDone = saved.completedAt
                        if (saved.totalElapsed != null) timer.totalElapsed = saved.totalElapsed
                        if (saved.remainingSec != null) timer.remainingSec = saved.remainingSec
                    } else {
                        timer.isAvailable = false
                    }
                }
                // Para timers normales, mantener el estado actual sin cambios
            })

            context.commit('setTimers', timersCopy)
            return Promise.resolve()
        }
    },
    formatTimers(context, params) {
        if (!Array.isArray(params)) {
            return
        }

        const locationNow = getLocationMoment(context.rootGetters)

        function floatToTimeStamp(float, offsetDays = 0) {
            return floatToLocationTimestamp(locationNow, float, offsetDays)
        }

        function timeToDecimal(t) {
            var arr = t.split(':')
            var dec = parseInt((arr[1] / 6) * 10, 10)

            return parseFloat(parseInt(arr[0], 10) + '.' + (dec < 10 ? '0' : '') + dec)
        }

        const timers = Object.values(params)

        const formattedTimers = timers.map((timer) => {
            const periodicity = JSON.parse(timer.periodicity)

            if (periodicity.typePeriodicity === 'manual') {
                let durationMinutes = 0
                if (Array.isArray(timer.times) && timer.times.length) {
                    durationMinutes = Number(timer.times[0]) || 0
                } else if (timer.times && typeof timer.times.iv !== 'undefined') {
                    durationMinutes = Number(timer.times.iv) || 0
                } else {
                    durationMinutes = Number(timer.times) || 0
                }

                const saved = (context.state.manualTimersState && context.state.manualTimersState[timer.id]) || null
                let isAvailable = false
                let lastAlarmDone = null

                if (saved) {
                    if (saved.isFinished && !saved.actionCompleted) {
                        isAvailable = true
                    }
                    if (saved.completedAt) {
                        lastAlarmDone = saved.completedAt
                    }
                }

                return {
                    ...timer,
                    isManual: true,
                    durationMinutes,
                    times: [],
                    previousAlarm: null,
                    currentPendingAlarm: null,
                    nextAlarm: null,
                    lastAlarmDone,
                    nextAlarmText: null,
                    hideTimer: false
                }
            }

            let formattedAlarms
            let nextAlarmText
            let nextAlarmText2 = false
            if (JSON.parse(timer.periodicity).typePeriodicity === 'daily') {
                if (typeof timer.times.p !== 'undefined') {
                    let planification = timer.times.p
                    let scheduleLocation = context.rootGetters['login/getConfig'].schedule
                    formattedAlarms = []
                    if (scheduleLocation && scheduleLocation.schedule) {
                        const days = ['u', 'm', 't', 'w', 'r', 'f', 's']
                        const today = locationNow.day()
                        let day = days[today]
                        let type = planification.type
                        let schedule = scheduleLocation.schedule[day][type]
                        // let schedule = scheduleLocation.schedule[day].type && scheduleLocation.schedule[day].c ? scheduleLocation.schedule[day].c : scheduleLocation.schedule[day] && scheduleLocation.schedule[day].r ? scheduleLocation.schedule[day].r : false
                        if (schedule) {
                            let start_date,
                                end_date = false

                            if (timer.times.p.mode == 's') {
                                start_date = parseLocationTimeOnDay(locationNow, schedule.s)
                                end_date = parseLocationTimeOnDay(locationNow, schedule.e)
                            } else {
                                start_date = parseLocationTimeOnDay(locationNow, schedule.e, -1)
                                end_date = parseLocationTimeOnDay(locationNow, schedule.e)
                            }

                            if (end_date.isBefore(start_date)) {
                                end_date = end_date.add(1, 'days')
                            }

                            //QUE PASA SI ESTAMOS EN LA MADRUGADA Y LA HORA DE FIN ES MENOR A LA DE INICIO
                            if (!locationNow.isBetween(start_date, end_date)) {
                                let day2 = today > 0 ? days[today - 1] : days[6]
                                let start_date2,
                                    end_date2 = false
                                if (timer.times.p.mode == 's') {
                                    start_date2 = parseLocationTimeOnDay(locationNow, schedule.s)
                                    end_date2 = parseLocationTimeOnDay(locationNow, schedule.e)
                                } else {
                                    start_date2 = parseLocationTimeOnDay(locationNow, schedule.e, -1)
                                    end_date2 = parseLocationTimeOnDay(locationNow, schedule.e)
                                }
                                if (end_date2.isBefore(start_date2)) {
                                    end_date2 = end_date2.add(1, 'days')
                                }
                                //SOBREESCRIBIR LOS VALORES
                                if (locationNow.isBetween(start_date, end_date)) {
                                    start_date = start_date2
                                    end_date = end_date2
                                    day = day2
                                }
                            }

                            // PREPARAMOS PRIMER ALARMA
                            start_date = start_date.add(planification.s, 'minutes')
                            if (start_date.isBefore(locationNow)) {
                                start_date.add(1, 'days')
                            }
                            formattedAlarms.push({ float: timeToDecimal(start_date.format('HH:mm')), timestamp: start_date.valueOf() })
                            // if (timer.times.p.mode == 'e' && timer.times.p.s == '-420') log('TEST', start_date)
                            // PREPARAMOS EL BUCLE
                            start_date.add(planification.i.e, 'minutes')
                            let it = planification.i.r
                            while (start_date.isBefore(end_date) && it > 0) {
                                formattedAlarms.push({ float: timeToDecimal(start_date.format('HH:mm')), timestamp: start_date.valueOf() })
                                start_date.add(planification.i.e, 'minutes')
                                it--
                            }
                        }
                    }
                    nextAlarmText2 = true
                } else {
                    // log(JSON.parse(JSON.stringify(timer)))
                    formattedAlarms = timer.times.map((float) => {
                        const formattedTime = { float: float, timestamp: floatToTimeStamp(float) }
                        return formattedTime
                    })
                }
                const nextAlarmValue = parseServerDateTime(timer.earliest_alarm_date)
                const currentDate = locationNow.valueOf()

                if (isValidServerDateTime(nextAlarmValue) && nextAlarmValue > currentDate) {
                    nextAlarmText = formatTimerAlarmFromStore(moment.utc(nextAlarmValue), { emptyValue: '', now: locationNow })
                }
            } else {
                formattedAlarms = timer.times.map((float) => {
                    const periodicity = JSON.parse(timer.periodicity)
                    const time = moment.utc(floatToTimeStamp(timer.times[0], 1)).format('HH:mm') // Hora de la alarma
                    const currentDate = locationNow.format('YYYY-MM-DD') // Fecha actual

                    const nextAlarm = getNextAlarmDate(periodicity.days, time, periodicity.typePeriodicity, periodicity.week, currentDate, locationNow)
                    // log(JSON.parse(JSON.stringify(nextAlarm)))
                    nextAlarmText = nextAlarm.nextAlarmDateTime

                    const formattedTime = { float: float, timestamp: floatToTimeStamp(float, nextAlarm.daysRemaining) }
                    return formattedTime
                })
            }

            // Obtener los timestamps de mañana, y si no existen crearlos (para evitar contador en 00:00)
            let closestNextTimestamp = null
            let smallestDifference = Infinity
            const currentTimestamp = locationNow.valueOf()

            for (let i = 0; i < formattedAlarms.length; i++) {
                const timeDifference = formattedAlarms[i].timestamp - currentTimestamp
                if (timeDifference > 0 && timeDifference < smallestDifference) {
                    smallestDifference = timeDifference
                    closestNextTimestamp = formattedAlarms[i].timestamp
                }
            }
            if (closestNextTimestamp === null) {
                if (JSON.parse(timer.periodicity).typePeriodicity === 'daily') {
                    if (typeof timer.times.p !== 'undefined') {
                        let planification = timer.times.p
                        let scheduleLocation = context.rootGetters['login/getConfig'].schedule
                        formattedAlarms = []
                        if (scheduleLocation && scheduleLocation.schedule) {
                            const days = ['u', 'm', 't', 'w', 'r', 'f', 's']
                            const today = locationNow.day()
                            let day = days[today]
                            let type = planification.type
                            let schedule = scheduleLocation.schedule[day][type]
                            if (schedule) {
                                let start_date,
                                    end_date = false
                                if (timer.times.p.mode == 's') {
                                    start_date = parseLocationTimeOnDay(locationNow, schedule.s)
                                    end_date = parseLocationTimeOnDay(locationNow, schedule.e)
                                } else {
                                    start_date = parseLocationTimeOnDay(locationNow, schedule.e)
                                    end_date = parseLocationTimeOnDay(locationNow, schedule.e)
                                }

                                if (end_date.isBefore(start_date)) {
                                    end_date = end_date.add(1, 'days')
                                }

                                //QUE PASA SI ESTAMOS EN LA MADRUGADA Y LA HORA DE FIN ES MENOR A LA DE INICIO
                                if (!locationNow.isBetween(start_date, end_date)) {
                                    let day2 = today > 0 ? days[today - 1] : days[6]
                                    let type2 = planification.type
                                    let schedule2 = scheduleLocation.schedule[day2][type2]
                                    // let schedule2 = planification.type == 'c' ? (scheduleLocation.schedule[day2].c ? scheduleLocation.schedule[day2].c : false) : scheduleLocation.schedule[day2].r ? scheduleLocation.schedule[day2].r : false
                                    if (schedule2) {
                                        let start_date2,
                                            end_date2 = false
                                        if (timer.times.p.mode == 's') {
                                            start_date2 = parseLocationTimeOnDay(locationNow, schedule.s)
                                            end_date2 = parseLocationTimeOnDay(locationNow, schedule.e)
                                        } else {
                                            start_date2 = parseLocationTimeOnDay(locationNow, schedule.e)
                                            end_date2 = parseLocationTimeOnDay(locationNow, schedule.e)
                                        }
                                        if (end_date2.isBefore(start_date2)) {
                                            end_date2 = end_date2.add(1, 'days')
                                        }
                                        //SOBREESCRIBIR LOS VALORES
                                        if (locationNow.isBetween(start_date, end_date)) {
                                            start_date = start_date2
                                            end_date = end_date2
                                            day = day2
                                        }
                                    }
                                }

                                // PREPARAMOS PRIMER ALARMA
                                start_date = start_date.add(planification.s, 'minutes')
                                if (start_date.isBefore(locationNow)) {
                                    start_date.add(1, 'days')
                                }
                                formattedAlarms.push({ float: timeToDecimal(start_date.format('HH:mm')), timestamp: start_date.valueOf() })
                                // PREPARAMOS EL BUCLE
                                start_date.add(planification.i.e, 'minutes')
                                let it = planification.i.r
                                while (start_date.isBefore(end_date) && it > 0) {
                                    formattedAlarms.push({ float: timeToDecimal(start_date.format('HH:mm')), timestamp: start_date.valueOf() })
                                    start_date.add(planification.i.e, 'minutes')
                                    it--
                                }
                            }
                        }
                    } else {
                        const formattedAlarmsTomorrow = timer.times.map((float) => {
                            return { float: float, timestamp: floatToTimeStamp(float, 1) }
                        })
                        formattedAlarms.push(...formattedAlarmsTomorrow)
                    }
                } else {
                    const periodicity = JSON.parse(timer.periodicity)
                    const time = moment.utc(floatToTimeStamp(timer.times[0], 1)).format('HH:mm') // Hora de la alarma
                    const currentDate = locationNow.format('YYYY-MM-DD') // Fecha actual

                    const nextAlarm = getNextAlarmDate(periodicity.days, time, periodicity.typePeriodicity, periodicity.week, currentDate, locationNow)
                    nextAlarmText = nextAlarm.nextAlarmDateTime

                    const formattedAlarmsTomorrow = timer.times.map((float) => {
                        return { float: float, timestamp: floatToTimeStamp(float, nextAlarm.daysRemaining) }
                    })
                    formattedAlarms.push(...formattedAlarmsTomorrow)
                }
            }

            const formattedTimer = {
                ...timer,
                times: formattedAlarms,
                isAvailable: false,
                previousAlarm: null,
                currentPendingAlarm: null,
                nextAlarm: null,
                lastAlarmDone: null,
                nextAlarmText: nextAlarmText,
                hideTimer: false
            }
            return JSON.parse(JSON.stringify(formattedTimer))
        })

        // setear Timers con los times formateados:
        context.commit('setTimers', formattedTimers)
        db_timers.clear().then(() => {
            formattedTimers.forEach((value, i) => {
                db_timers.setItem(value.id, { ...value, i })
            })
        })
    },

    updateTimers(context, params) {
        if (!Array.isArray(state.timers)) {
            return
        }

        const currentTimestamp = getLocationTimestamp(context.rootGetters)
        const locationNow = getLocationMoment(context.rootGetters)

        const updatedTimers = state.timers.map((timer) => {
            // Verificar si es timer manual PRIMERO y de forma exclusiva
            let isManualTimer = false
            try {
                const periodicity = JSON.parse(timer.periodicity)
                isManualTimer = periodicity.typePeriodicity === 'manual' || timer.isManual === true
            } catch (e) {
                isManualTimer = timer.isManual === true
            }

            if (isManualTimer) {
                // Lógica exclusiva para timers manuales
                const saved = context.state.manualTimersState && context.state.manualTimersState[timer.id] ? context.state.manualTimersState[timer.id] : null
                if (saved) {
                    // needs signature only if finished and not completed
                    timer.isAvailable = !!(saved.isFinished && !saved.actionCompleted)
                    if (saved.completedAt && !timer.lastAlarmDone) {
                        timer.lastAlarmDone = saved.completedAt
                    }
                } else {
                    // initial manual state: not available until it finishes
                    timer.isAvailable = false
                }
                return timer // Retornar inmediatamente, sin más procesamiento
            }

            // Resto de la lógica original para timers repetitivos
            const periodicity = JSON.parse(timer.periodicity)
            const isWeeklyOrBiweekly = ['weekly', 'biweekly', 'monthly', 'quarterly'].includes(periodicity.typePeriodicity)
            const dateCreated = parseServerDateTime(timer.earliest_alarm_date)

            if (isWeeklyOrBiweekly) {
                // Para timers semanales y quincenales
                const time = moment.utc(timer.times[0].timestamp).format('HH:mm')
                const currentDate = locationNow.format('YYYY-MM-DD')
                const previousAlarm = getPreviousAlarmDate(periodicity.days, time, periodicity.typePeriodicity, periodicity.week, currentDate, locationNow)
                timer.previousAlarm = previousAlarm.previousAlarmTimestamp

                const nextAlarm = getNextAlarmDate(periodicity.days, time, periodicity.typePeriodicity, periodicity.week, currentDate, locationNow)
                timer.nextAlarm = nextAlarm.nextAlarmTimestamp
                // if (periodicity.typePeriodicity === 'monthly') log('monthly', timer.lastAlarmDone, timer.previousAlarm, timer.currentPendingAlarm, currentTimestamp, dateCreated)
                // if (timer.id === 'Snh1MjF6TzBWN1Jpd0cycnNHZ0J2UT09') {
                //   log(
                //     'timer', timer,
                //     'timer problem',
                //     'lastAlarmDone:', timer.lastAlarmDone ? moment(timer.lastAlarmDone).format('DD/MM/YY HH:mm') : null,
                //     'previousAlarm:', timer.previousAlarm ? moment(timer.previousAlarm).format('DD/MM/YY HH:mm') : null,
                //     'currentPendingAlarm:', timer.currentPendingAlarm ? moment(timer.currentPendingAlarm).format('DD/MM/YY HH:mm') : null,
                //     'currentTimestamp:', moment(currentTimestamp).format('DD/MM/YY HH:mm'),
                //     'dateCreated:', moment(dateCreated).format('DD/MM/YY HH:mm')
                //   );
                // }
                if (isValidServerDateTime(dateCreated) && !timer.lastAlarmDone && dateCreated >= timer.previousAlarm && dateCreated >= currentTimestamp && !timer.currentPendingAlarm) {
                    timer.isAvailable = false
                    timer.currentPendingAlarm = null
                    timer.nextAlarmText = nextAlarm.nextAlarmDateTime
                } else if (isValidServerDateTime(dateCreated) && !timer.lastAlarmDone && dateCreated >= timer.previousAlarm && dateCreated <= timer.previousAlarm) {
                    timer.isAvailable = dateCreated <= locationNow.valueOf()
                    timer.currentPendingAlarm = dateCreated
                    timer.nextAlarm = dateCreated
                    timer.nextAlarmText = formatTimerAlarmFromStore(moment.utc(dateCreated), { emptyValue: '', now: locationNow })
                } else if (isValidServerDateTime(dateCreated) && timer.lastAlarmDone < timer.previousAlarm && dateCreated >= currentTimestamp) {
                    timer.isAvailable = true
                    timer.currentPendingAlarm = timer.previousAlarm
                    timer.nextAlarmText = previousAlarm.previousAlarmDateTimeText
                } else if (timer.previousAlarm && timer.previousAlarm < currentTimestamp && (!timer.lastAlarmDone || timer.lastAlarmDone < timer.previousAlarm)) {
                    timer.isAvailable = true
                    timer.currentPendingAlarm = timer.previousAlarm
                    timer.nextAlarmText = previousAlarm.previousAlarmDateTimeText
                } else {
                    timer.isAvailable = false
                    timer.currentPendingAlarm = null
                    timer.nextAlarmText = nextAlarm.nextAlarmDateTime
                }
            } else {
                // Para timers diarios
                const lastDone = Number(timer.lastAlarmDone) || 0
                const timestamps = timer.times.map((time) => time.timestamp)
                const dateCreated = parseServerDateTime(timer.earliest_alarm_date)

                // Alarma pasada más reciente que aún no fue firmada
                let closestPreviousAlarmTimestamp = null
                let smallestPrevDiff = Infinity

                for (let i = 0; i < timestamps.length; i++) {
                    const ts = timestamps[i]
                    if (ts <= lastDone) continue
                    const timeDifference = currentTimestamp - ts
                    if (timeDifference >= 0 && timeDifference < smallestPrevDiff) {
                        smallestPrevDiff = timeDifference
                        closestPreviousAlarmTimestamp = ts
                    }
                }

                timer.previousAlarm = closestPreviousAlarmTimestamp

                // Próxima alarma futura (calcular antes de decidir isAvailable)
                let closestNextTimestamp = null
                let smallestNextDiff = Infinity

                for (let i = 0; i < timestamps.length; i++) {
                    const timeDifference = timestamps[i] - currentTimestamp
                    if (timeDifference > 0 && timeDifference < smallestNextDiff) {
                        smallestNextDiff = timeDifference
                        closestNextTimestamp = timestamps[i]
                    }
                }

                if (closestNextTimestamp !== null) {
                    timer.nextAlarm = closestNextTimestamp
                } else if (timestamps.length > 0) {
                    const tomorrowTimestamps = timestamps.map((ts) => moment.utc(ts).add(1, 'days').valueOf())
                    timer.nextAlarm = Math.min(...tomorrowTimestamps)
                } else {
                    const earliestMs = parseServerDateTime(timer.earliest_alarm_date)
                    timer.nextAlarm = isValidServerDateTime(earliestMs) ? earliestMs : timestamps[0] || null
                }

                const timerAlreadyActive = !isValidServerDateTime(dateCreated) || dateCreated <= currentTimestamp

                if (closestPreviousAlarmTimestamp !== null && timerAlreadyActive) {
                    // Alarma de hoy vencida y sin firmar, solo si el timer ya está activo
                    timer.currentPendingAlarm = closestPreviousAlarmTimestamp
                    timer.isAvailable = true
                } else if (!timer.lastAlarmDone && timerAlreadyActive && closestPreviousAlarmTimestamp === null) {
                    // Ninguna alarma de hoy ha pasado; buscar pendiente de ayer
                    let closestYesterday = null
                    smallestPrevDiff = Infinity
                    const timestampsYesterday = timestamps.map((ts) => moment.utc(ts).subtract(1, 'days').valueOf())

                    for (let i = 0; i < timestampsYesterday.length; i++) {
                        const ts = timestampsYesterday[i]
                        if (ts <= lastDone) continue
                        const timeDifference = currentTimestamp - ts
                        if (timeDifference >= 0 && timeDifference < smallestPrevDiff) {
                            smallestPrevDiff = timeDifference
                            closestYesterday = ts
                        }
                    }

                    if (closestYesterday !== null) {
                        timer.isAvailable = true
                        timer.currentPendingAlarm = closestYesterday
                        timer.previousAlarm = closestYesterday
                    } else {
                        timer.isAvailable = false
                        timer.currentPendingAlarm = null
                    }
                } else {
                    timer.isAvailable = false
                    timer.currentPendingAlarm = null
                }
            }

            return timer
        })

        context.commit('setTimers', updatedTimers)
    },

    setupInterval(context, params) {
        const ms = params && params.intervalMs != null ? params.intervalMs : state.intervalMs != null ? state.intervalMs : TIMER_TICK_SLOW_MS

        if (ms === state.intervalMs && state.interval) {
            context.commit('tickLocationNow')
            return context.dispatch('updateTimers')
        }

        if (state.interval) {
            clearInterval(state.interval)
        }

        state.intervalMs = ms

        const interval = setInterval(() => {
            context.commit('tickLocationNow')
            context.dispatch('updateTimers')
        }, ms)

        context.commit('setInterval', interval)
        context.commit('tickLocationNow')

        return context.dispatch('updateTimers')
    },
    setHideAlarm(context, params) {
        const { alarmId } = params
        const timerIndex = state.timers.findIndex((timer) => timer.id === alarmId)
        if (timerIndex !== -1) {
            Vue.set(state.timers[timerIndex], 'hideTimer', true)
        } else {
            console.error(`Timer with id ${alarmId} not found`)
        }
    },

    resetAllHideTimers(context) {
        log('Timers antes:', state.timers) // Verifica el array completo
        state.timers.forEach((timer, index) => {
            isMoreThanSixHours(timer.nextAlarm, timer.id)
            log(`Procesando timer ${index}:`, timer)
            timer.hideTimer = false
        })
        log('Timers después:', state.timers) // Verifica los cambios
    },
    backAllHideTimers(context) {
        log('Timers antes:', state.timers) // Verifica el array completo
        state.timers.forEach((timer, index) => {
            log(`Procesando timer ${index}:`, timer)
            timer.hideTimer = isMoreThanSixHours(timer.nextAlarm, timer.isAvailable)
        })
        log('Timers después:', state.timers) // Verifica los cambios
    },
    async setAlarmAsDone(context, params) {
        let requireSync = !(fnCheckConnection() && context.rootGetters.getFastConnection) && context.rootGetters.getOffline ? true : undefined
        const { alarm, checkDateTimestamp } = params
        const signInstant = checkDateTimestamp != null ? new Date(checkDateTimestamp) : new Date()
        const locationAtSign = getLocationMoment(context.rootGetters, signInstant)
        const now = locationAtSign.valueOf()

        const employeeId = context.rootGetters['loginUser/getLocalEmployee']
        const businessDate = locationAtSign.format('YYYY-MM-DD')
        const checkDate = locationAtSign.format('YYYY-MM-DD HH:mm:ss')

        // MANUALES
        if (alarm.isManual === true) {
            const srcTimer = context.getters.getTimerById(alarm.id)
            const initialSec = srcTimer && srcTimer.durationMinutes ? Number(srcTimer.durationMinutes) * 60 : 0

            // 1) Actualización optimista inmediata en Vuex + localStorage
            const manualTimersState = { ...(context.state.manualTimersState || {}) }
            manualTimersState[alarm.id] = {
                hasStarted: false,
                isRunning: false,
                isFinished: false,
                actionCompleted: true, // quedó firmado
                completedAt: now,

                targetSeconds: initialSec,
                elapsedSeconds: 0,
                remainingSeconds: initialSec,

                // compat CountDownManual
                remainingSec: initialSec,
                totalElapsed: 0,
                initialSec: initialSec,

                lastUpdated: persistenceTimestamp(),
                fromServer: false
            }
            context.commit('setManualTimersState', manualTimersState)
            try {
                localStorage.setItem('manualTimersState', JSON.stringify(manualTimersState))
            } catch (_) {}

            // 2) Reflejar en el listado de timers
            const timersCopy = structuredClone(context.state.timers || [])
            const updated = timersCopy.map((t) => {
                if (t.id !== alarm.id) return t
                return {
                    ...t,
                    isAvailable: false, // no pide firma; quedó listo para iniciar de nuevo
                    currentPendingAlarm: null,
                    lastAlarmDone: now,
                    remainingSec: initialSec,
                    totalElapsed: 0
                }
            })
            context.commit('setTimers', updated)
            db_timers
                .clear()
                .then(() => {
                    updated.forEach((t) => db_timers.setItem(t.id, t))
                })
                .catch((e) => console.error('persist manual check setAlarmAsDone:', e))

            // 3) Enviar al backend usando el endpoint correcto (no bloquear UI si falla)
            const postData = {
                ...(employeeId && { employee_id: employeeId }),
                check_date: checkDate
            }

            // usa el endpoint de manuales
            return api
                .post(`admin/timer-op/check/${alarm.id}`, postData)
                .then(() => true)
                .catch((e) => {
                    console.error('POST manual check setAlarmAsDone:', e)
                    // dejamos true para no romper UX si el server devuelve formato/estado inesperado
                    return true
                })
        }

        // REPETITIVOS
        if (!alarm.isAvailable || !alarm.currentPendingAlarm) return false
        if (alarm.lastAlarmDone && alarm.lastAlarmDone >= alarm.currentPendingAlarm) return false

        const pendingTs = alarm.currentPendingAlarm
        const timersCopy = structuredClone(context.state.timers || [])
        let updatedAlarm = null

        const advanced = timersCopy.map((t) => {
            if (t.id !== alarm.id) return t
            updatedAlarm = { ...t, lastAlarmDone: pendingTs, isAvailable: false, currentPendingAlarm: null, lastUpdated: persistenceTimestamp() }
            return updatedAlarm
        })

        context.commit('setTimers', advanced)
        db_timers
            .clear()
            .then(() => {
                advanced.forEach((t) => db_timers.setItem(t.id, t))
            })
            .catch((e) => console.error('persist repetitive setAlarmAsDone:', e))

        const alarmData = {
            employee_id: employeeId,
            alarm_date: moment.utc(pendingTs).format('YYYY-MM-DD HH:mm:ss'),
            business_date: moment.utc(pendingTs).format('YYYY-MM-DD'),
            check_date: checkDate
        }

        if (requireSync) {
            let id = uuidv4()
            return db_checks_to_sync.setItem(id, { ...alarmData, alarm_id: updatedAlarm.id, id, timestamp: now }).then(() => {
                context.dispatch('getCountSyncItems', {}, { root: true })
                return true
            })
        }

        return api
            .post(`timer-op/timers/${updatedAlarm.id}`, alarmData)
            .then(async () => {
                await context.dispatch('getLastTimesDone')
                await context.dispatch('updateTimers')
                return true
            })
            .catch((e) => {
                console.error('POST repetitive setAlarmAsDone:', e)
                return true
            })
    },
    allowManualTimerRestart(context, { timerId }) {
        const manualTimersState = { ...context.state.manualTimersState }

        if (manualTimersState[timerId]) {
            // Reset del estado para permitir nuevo uso
            delete manualTimersState[timerId]
            context.commit('setManualTimersState', manualTimersState)
            localStorage.setItem('manualTimersState', JSON.stringify(manualTimersState))

            // Actualizar el timer para que vuelva a estar disponible
            const timersCopy = structuredClone(state.timers)
            const updatedTimers = timersCopy.map((timer) => {
                if (timer.id === timerId && timer.isManual) {
                    return {
                        ...timer,
                        isAvailable: true,
                        lastAlarmDone: null
                    }
                }
                return timer
            })

            context.commit('setTimers', updatedTimers)
        }
    },
    syncChecks(context, params) {
        let checks = []
        db_checks_to_sync
            .iterate(function (value) {
                checks.push(value)
            })
            .then(async function () {
                const groupedChecks = checks.reduce((acc, check) => {
                    acc[check.alarm_id] = acc[check.alarm_id] || []
                    acc[check.alarm_id].push(check)
                    return acc
                }, {})

                Object.values(groupedChecks).forEach((group) => {
                    group.sort((a, b) => new Date(a.alarm_date) - new Date(b.alarm_date))
                })

                const batches = []

                let batch = {}
                let batchCount = 0

                for (const [alarm_id, group] of Object.entries(groupedChecks)) {
                    while (group.length > 0) {
                        if (!batch[alarm_id]) {
                            batch[alarm_id] = []
                        }

                        const toAdd = group.splice(0, 10 - batchCount)
                        batch[alarm_id] = batch[alarm_id].concat(toAdd)
                        batchCount += toAdd.length

                        if (batchCount >= 10) {
                            batches.push(batch)
                            batch = {}
                            batchCount = 0
                        }
                    }
                }

                if (batchCount > 0) {
                    batches.push(batch)
                }

                for (const batch of batches) {
                    await api
                        .post(`timer-op/sync`, { timers: JSON.stringify(batch) })
                        .then((res) => {
                            if (res.status == true) {
                                const idsToDelete = Object.values(batch)
                                    .flat()
                                    .map((check) => check.id)
                                idsToDelete.forEach((id) => db_checks_to_sync.removeItem(id))
                                context.dispatch('getCountSyncItems', {}, { root: true })
                            }
                        })
                        .catch(function (error) {
                            console.error(error)
                            return false
                        })
                }

                context.dispatch('getCountSyncItems', {}, { root: true })
            })
    },
    getCountSync(context) {
        return new Promise((resolve, reject) => {
            var count = 0
            db_checks_to_sync
                .iterate(function (value, key, iterationNumber) {
                    count++
                })
                .then(function () {
                    resolve(count)
                })
        })
    }
}

function isMoreThanSixHours(nextAlarm, isAvailable) {
    var isMoreThanSixHours = false
    if (nextAlarm && !isAvailable) {
        const targetTime = new Date(nextAlarm)
        const now = getLocationTimestamp(store.getters)
        const timeRemaining = targetTime - now

        const totalSeconds = Math.floor(timeRemaining / 1000)
        const totalMinutes = Math.floor(totalSeconds / 60)
        const totalHours = Math.floor(totalMinutes / 60)

        const moreThanSixHours = totalHours >= getTimeFilter()
        if (isMoreThanSixHours !== moreThanSixHours) {
            isMoreThanSixHours = moreThanSixHours
        }
    }
    return isMoreThanSixHours
}

function getTimeFilter() {
    const config = store.state.login.config
    return config.Timersh || config.Timersh > 0 ? config.Timersh : 6
}

function getNextAlarmDate(days, time, frequency, week = null, currentDate = null, locationNow = null) {
    // log(`getNextAlarDate('${days}', ${time}, ${frequency}, ${week})`)
    const currentTime = locationNow || moment.utc()
    const now = moment.utc(currentDate || currentTime.format('YYYY-MM-DD'), 'YYYY-MM-DD').startOf('day')
    const [targetHour, targetMinute] = time.split(':').map(Number)
    let nextAlarm = null

    if (frequency === 'quarterly') {
        var months = days
    }

    // Lógica actual para frecuencias semanal y quincenal
    for (let i = 0; i <= 7; i++) {
        const dayOffset = (now.day() + i) % 7
        if (days.includes((dayOffset + 6) % 7)) {
            const potentialDate = now.clone().add(i, 'days').set({
                hour: targetHour,
                minute: targetMinute
            })

            if (i === 0 && currentTime.isBefore(potentialDate)) {
                nextAlarm = potentialDate
            } else if (i > 0) {
                nextAlarm = potentialDate
            }

            if (nextAlarm) break
        }
    }

    if (nextAlarm && nextAlarm.isSameOrBefore(currentTime, 'minute')) {
        if (frequency === 'biweekly') {
            nextAlarm = nextAlarm.add(14, 'days')
        } else {
            nextAlarm = nextAlarm.add(7, 'days')
        }
    }
    // Caso Mensual
    if (frequency === 'monthly' && week !== null) {
        const daysOfWeek = days.map((day) => (day + 6) % 7) // Convertimos días a formato moment (0=Domingo, 1=Lunes...)
        let foundValidAlarm = false

        // Inicio de la semana especificada del mes
        const startOfMonth = now.clone().startOf('month')
        let weekStartDate = startOfMonth.clone().add(week - 1, 'weeks')

        // Buscar el día de la semana más cercano en la primera semana
        for (let i = 0; i < daysOfWeek.length; i++) {
            const targetDayOfWeek = daysOfWeek[i]
            let targetAlarm = weekStartDate.clone().day(targetDayOfWeek).set({
                hour: targetHour,
                minute: targetMinute,
                second: 0
            })

            // Si la alarma es antes de la fecha y hora actuales, mover a la siguiente semana
            if (targetAlarm.isBefore(currentTime)) {
                targetAlarm.add(7, 'days')
            }

            // Al encontrar un próximo día y hora válidos, establecemos la alarma
            if (targetAlarm.isAfter(currentTime)) {
                nextAlarm = targetAlarm
                foundValidAlarm = true
                break
            }
        }

        // Si no se encontró una alarma válida, buscamos en la próxima semana permitida
        if (!foundValidAlarm) {
            weekStartDate = weekStartDate.add(1, 'week').startOf('week')

            for (let i = 0; i < daysOfWeek.length; i++) {
                const targetDayOfWeek = daysOfWeek[i]
                const targetAlarm = weekStartDate.clone().day(targetDayOfWeek).set({
                    hour: targetHour,
                    minute: targetMinute,
                    second: 0
                })

                nextAlarm = targetAlarm
                break
            }
        }
    }

    // Nuevo caso: Trimestral
    if (frequency === 'quarterly' && Array.isArray(days) && days.length > 0) {
        const months = days
        const currentMonth = now.month()
        let foundNextQuarterDay = false

        // Revisamos si el mes actual está en el arreglo de meses objetivo
        if (months.includes(currentMonth)) {
            // Si la hora actual aún no ha pasado, programa la alarma para hoy
            if (targetHour > currentTime.hour() || (targetHour === currentTime.hour() && targetMinute > currentTime.minute())) {
                nextAlarm = now.clone().set({
                    hour: targetHour,
                    minute: targetMinute,
                    second: 0
                })
                foundNextQuarterDay = true
            } else {
                // Si la hora actual ya ha pasado, programa la alarma para el próximo día
                nextAlarm = now.clone().add(1, 'day').set({
                    hour: targetHour,
                    minute: targetMinute,
                    second: 0
                })
                foundNextQuarterDay = true
            }
        }

        // Si no encontramos un día válido en el mes actual, busca el primer mes válido en el próximo año
        if (!foundNextQuarterDay) {
            for (let i = 0; i < months.length; i++) {
                const month = months[i]
                if (month > currentMonth || (month === currentMonth && (targetHour > currentTime.hour() || (targetHour === currentTime.hour() && targetMinute > currentTime.minute())))) {
                    nextAlarm = now.clone().month(month).date(1).set({
                        hour: targetHour,
                        minute: targetMinute,
                        second: 0
                    })
                    foundNextQuarterDay = true
                    break
                }
            }

            // Si no se encuentra un mes válido, programa para el primer mes objetivo del próximo año
            if (!foundNextQuarterDay) {
                nextAlarm = now.clone().add(1, 'year').month(months[0]).date(1).set({
                    hour: targetHour,
                    minute: targetMinute,
                    second: 0
                })
            }
        }
    }

    const totalMinutes = nextAlarm.diff(currentTime, 'minutes')
    const exactDays = totalMinutes / (24 * 60)
    const daysRemaining = Math.floor(exactDays)
    const hoursRemaining = Math.floor((exactDays - daysRemaining) * 24)

    return {
        nextAlarmDateTime: formatTimerAlarmFromStore(nextAlarm, { emptyValue: '', now: currentTime }),
        nextAlarmTimestamp: nextAlarm.valueOf(),
        daysRemaining: daysRemaining + hoursRemaining / 24,
        interval: exactDays.toFixed(2)
    }
}

function getPreviousAlarmDate(days, time, frequency, week = null, currentDate = null, locationNow = null) {
    const currentTime = locationNow || moment.utc()
    const now = moment.utc(currentDate || currentTime.format('YYYY-MM-DD'), 'YYYY-MM-DD').startOf('day')
    const [targetHour, targetMinute] = time.split(':').map(Number)
    let previousAlarm = null

    // Lógica actual para frecuencias semanal y quincenal
    for (let i = 0; i <= 7; i++) {
        const dayOffset = (now.day() - i + 7) % 7
        if (days.includes((dayOffset + 6) % 7)) {
            const potentialDate = now.clone().subtract(i, 'days').set({
                hour: targetHour,
                minute: targetMinute
            })

            if (i === 0 && currentTime.isAfter(potentialDate)) {
                previousAlarm = potentialDate
            } else if (i > 0) {
                previousAlarm = potentialDate
            }

            if (previousAlarm) break
        }
    }

    if (previousAlarm && previousAlarm.isSameOrAfter(currentTime, 'minute')) {
        if (frequency === 'biweekly') {
            previousAlarm = previousAlarm.subtract(14, 'days')
        } else {
            previousAlarm = previousAlarm.subtract(7, 'days')
        }
    }

    // Nuevo caso: Mensual
    if (frequency === 'monthly' && week !== null) {
        const daysOfWeek = days.map((day) => (day + 6) % 7) // Convertimos días a formato moment (0=Domingo, 1=Lunes...)
        let foundValidAlarm = false

        // Inicio de la semana especificada del mes
        const startOfMonth = now.clone().startOf('month')
        let weekStartDate = startOfMonth.clone().add(week - 1, 'weeks')

        // Busca,ps el día de la semana más cercano en la primera semana
        for (let i = daysOfWeek.length - 1; i >= 0; i--) {
            const targetDayOfWeek = daysOfWeek[i]
            let targetAlarm = weekStartDate.clone().day(targetDayOfWeek).set({
                hour: targetHour,
                minute: targetMinute,
                second: 0
            })

            // Si la alarma es después de la fecha y hora actuales, mover a la semana anterior
            if (targetAlarm.isAfter(now)) {
                targetAlarm.subtract(7, 'days')
            }

            // Al encontrar un día y hora válidos antes de la fecha actual, establecemos la alarma
            if (targetAlarm.isBefore(now)) {
                previousAlarm = targetAlarm
                foundValidAlarm = true
                break
            }
        }

        // Si no se encontró una alarma válida, buscamos en la semana anterior permitida
        if (!foundValidAlarm) {
            weekStartDate = weekStartDate.subtract(1, 'week').startOf('week')

            for (let i = daysOfWeek.length - 1; i >= 0; i--) {
                const targetDayOfWeek = daysOfWeek[i]
                const targetAlarm = weekStartDate.clone().day(targetDayOfWeek).set({
                    hour: targetHour,
                    minute: targetMinute,
                    second: 0
                })

                previousAlarm = targetAlarm
                break
            }
        }
    }

    // Nuevo caso: Trimestral
    if (frequency === 'quarterly' && Array.isArray(days) && days.length > 0) {
        const months = days
        const currentMonth = now.month()
        let foundPreviousQuarterDay = false

        // Revisamos si el mes actual está en el arreglo de meses objetivo
        if (months.includes(currentMonth)) {
            // Si la hora actual ya ha pasado, programa la alarma para hoy
            if (targetHour < currentTime.hour() || (targetHour === currentTime.hour() && targetMinute <= currentTime.minute())) {
                previousAlarm = now.clone().set({
                    hour: targetHour,
                    minute: targetMinute,
                    second: 0
                })
                foundPreviousQuarterDay = true
            } else {
                // Si la hora actual aún no ha pasado, programa la alarma para el día anterior
                previousAlarm = now.clone().subtract(1, 'day').set({
                    hour: targetHour,
                    minute: targetMinute,
                    second: 0
                })
                foundPreviousQuarterDay = true
            }
        }

        // Si no encontramos un día válido en el mes actual, busca el último mes válido en el año pasado
        if (!foundPreviousQuarterDay) {
            for (let i = months.length - 1; i >= 0; i--) {
                const month = months[i]
                if (month < currentMonth || (month === currentMonth && (targetHour < currentTime.hour() || (targetHour === currentTime.hour() && targetMinute <= currentTime.minute())))) {
                    previousAlarm = now.clone().month(month).endOf('month').set({
                        hour: targetHour,
                        minute: targetMinute,
                        second: 0
                    })
                    foundPreviousQuarterDay = true
                    break
                }
            }

            // Si no se encuentra un mes válido, programa para el último mes objetivo del año pasado
            if (!foundPreviousQuarterDay) {
                previousAlarm = now
                    .clone()
                    .subtract(1, 'year')
                    .month(months[months.length - 1])
                    .endOf('month')
                    .set({
                        hour: targetHour,
                        minute: targetMinute,
                        second: 0
                    })
            }
        }
    }

    const totalMinutes = currentTime.diff(previousAlarm, 'minutes')
    const exactDays = totalMinutes / (24 * 60)
    const daysElapsed = Math.floor(exactDays)
    const hoursElapsed = Math.floor((exactDays - daysElapsed) * 24)

    return {
        previousAlarmDateTime: previousAlarm.format('YYYY-MM-DD HH:mm:ss'),
        previousAlarmDateTimeText: formatTimerAlarmFromStore(previousAlarm, { emptyValue: '', now: currentTime }),
        previousAlarmTimestamp: previousAlarm.valueOf()
    }
}

function loadTimersOffline(context) {
    let timers = []
    return db_timers
        .iterate(function (value, key, iterationNumber) {
            timers.push(value)
        })
        .then(function () {
            timers.sort((a, b) => a.i - b.i)

            // TTL de caché offline: lastUpdated siempre es epoch real del dispositivo (Date.now)
            const persistedNow = persistenceTimestamp()

            timers.forEach((timer) => {
                // Verificar si el estado guardado sigue siendo válido
                if (timer.lastUpdated && timer.lastAlarmDone) {
                    const timeSinceLastUpdate = persistedNow - timer.lastUpdated

                    // Si han pasado menos de 24 horas, confiar en el estado guardado
                    if (timeSinceLastUpdate < 24 * 60 * 60 * 1000) {
                        // Mantener el estado de isAvailable si fue guardado recientemente
                        if (typeof timer.isAvailable !== 'undefined') {
                            // El estado se mantiene como está guardado
                        }
                    } else {
                        // Si ha pasado mucho tiempo, recalcular
                        timer.isAvailable = false
                    }
                }
            })

            context.commit('setTimers', timers)
            return context.dispatch('updateTimers').then(() => {
                if (!context.state.interval) {
                    context.dispatch('setupInterval', { intervalMs: TIMER_TICK_SLOW_MS })
                }

                return true
            })
        })
        .catch((error) => {
            console.error('Error loading operational timers offline:', error)

            return false
        })
}

const mutations = {
    setManualTimersState(state, data) {
        state.manualTimersState = data
    },
    setTimers(state, payload) {
        state.timers = payload
        Vue.set(state, 'timers', payload)
    },
    setInterval(state, data) {
        state.interval = data
    },
    tickLocationNow(state) {
        state.locationNowTick++
    },
    setChecksToSync(state, data) {
        state.checksToSync = data
    },
    setCategories(state, categories) {
        state.categories = categories
        Vue.set(state, 'categories', categories)
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
